import React, {
    Fragment, useCallback, useState, useContext, useRef
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TranslatorContext } from '@jutro/locale';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication, RedirectAfterLogin, AUTH_ERRORS } from 'gw-digital-auth-react';
import { Loader } from '@jutro/components';
import { isEmpty } from '../LoginUtil';

import messages from './LoginComponent.messages';
import metadata from './LoginComponent.metadata.json5';
import styles from '../CommonLogin.module.scss';

function LoginComponent(props) {
    const {
        onForgotPassword, successPath, loginPath, pathAfterSignIn
    } = props;
    const translator = useContext(TranslatorContext);
    const [formData, updateFormData] = useState({});
    const [loginError, updateLoginError] = useState('');
    const [isSignInButtonClicked, setSignInButtonClicked] = useState(false);
    const { login: authLogin, isAuthInProgress } = useAuthentication();
    const { sendVerifyCode, verifyCodeReceived } = useAuthentication();
    const signInRef = useRef({});
    const [isLoginInProgress, setLoginInProgress] = useState(false);
    const [phoneNumber, updatePhoneNumber] = useState('');

    const login = useCallback(() => {
        const username = _.get(formData, 'username');
        const password = _.get(formData, 'password');
        const codeReceived = _.get(formData, 'codeReceived');
        setLoginInProgress(true);
        if (isEmpty(username) || isEmpty(password) || isEmpty(codeReceived)) {
            updateLoginError('fieldEmpty');
            return Promise.resolve();
        }
        if (codeReceived.length < 6 || codeReceived.length > 6 || isNaN(codeReceived)) {
            updateLoginError('invalidCode');
            return Promise.resolve();
        }

        updateLoginError(''); // reset errors

        return authLogin({
            username: username,
            password: password,
            codeReceived: codeReceived
        })
            .then(() => {
                // reset errors
                updateLoginError('');
                setLoginInProgress(false);
            })
            .catch((err) => {
                setLoginInProgress(false);
                switch (_.get(err, 'error')) {
                    case AUTH_ERRORS.login_failure: // credentials are wrong
                    case AUTH_ERRORS.notLoggedIn: // tokens check problem
                        updateLoginError('fieldsIncorrect');
                        break;
                    case AUTH_ERRORS.account_locked: {
                        // account is locked
                        updateLoginError('accountLocked');
                        break;
                    }
                    case AUTH_ERRORS.invalidVerificationCode: {
                        // Invalid verification code is entered
                        updateLoginError('invalidVerificationCode');
                        break;
                    }
                    default:
                        updateLoginError('invalidAttempt');
                        break;
                }
            });
    }, [authLogin, formData]);

    const sendVerificationCode = useCallback(() => {
        const username = _.get(formData, 'username');
        const password = _.get(formData, 'password');

        if (isEmpty(username) || isEmpty(password)) {
            updateLoginError('fieldEmpty');
            return Promise.resolve();
        }

        updateLoginError(''); // reset errors

        return sendVerifyCode({
            givenName: 'user',
            familyName: 'name',
            userName: username,
            email: 'user@name.com',
            password: password
        })
            .then((resp) => {
                console.log(resp);
                updatePhoneNumber(resp);
                setSignInButtonClicked(true);
            })
            .catch((err) => {
                setLoginInProgress(false);
                switch (_.get(err, 'error')) {
                    case AUTH_ERRORS.login_failure: // credentials are wrong
                    case AUTH_ERRORS.notLoggedIn: // tokens check problem
                        updateLoginError('fieldsIncorrect');
                        break;
                    case AUTH_ERRORS.account_locked: {
                        // account is locked
                        updateLoginError('accountLocked');
                        break;
                    }
                    default:
                        updateLoginError('invalidAttempt');
                        break;
                }
            });
    }, [sendVerifyCode, formData]);

    const verifyVerificationCode = useCallback(() => {
        const codeReceived = _.get(formData, 'codeReceived');

        if (isEmpty(codeReceived)) {
            updateLoginError('fieldEmpty');
            return Promise.resolve();
        }

        updateLoginError(''); // reset errors

        return verifyCodeReceived({
            codeReceived
        })
            .then((resp) => {
                console.log(resp);
                login();
            })
            .catch((err) => {
                console.log(err);
            });
    }, [verifyCodeReceived, formData]);

    const readValue = useCallback(
        (id, path) => {
            return _.get(formData, path);
        },
        [formData]
    );

    const writeValue = useCallback(
        (value, path) => {
            const nextFormData = _.cloneDeep(formData);
            _.set(nextFormData, path, value);
            updateFormData(nextFormData);
        },
        [formData]
    );

    const handleEnterKey = () => {
        signInRef.current.click();
    };
    if (isAuthInProgress) {
        return <Loader loaded={!isAuthInProgress} />;
    }

    const overrideProps = {
        '@field': {
            onValueChange: writeValue,
            onEnter: handleEnterKey
        },
        signIn: {
            trigger: sendVerificationCode,
            innerRef: signInRef
        },
        verifyCode: {
            trigger: login,
            innerRef: signInRef
        },
        linkToForgotPassword: {
            visible: !_.isUndefined(onForgotPassword),
            className: styles.forgotPassword
        },
        linkToSignup: {
            visible: !_.isUndefined(onForgotPassword),
            className: styles.forgotPassword
        },
        actionsWrapperSingleButton: {
            className: 'loginButton ' + classNames(styles.actionsWrapperSingleButton , {
                [styles.coupleButtonStyle]: !_.isUndefined(onForgotPassword),
                [styles.singleButtonStyle]: _.isUndefined(onForgotPassword)
            }),
            visible: isSignInButtonClicked !== true,
        },
        errorMsg: {
            content: translator(messages[loginError]),
            visible: !!loginError,
            role: 'alert',
            tabIndex: !!loginError ? '0' : '-1'
        },
        loginForm: {
            visible: isSignInButtonClicked !== true
        },
        verifyForm: {
            visible: isSignInButtonClicked === true
        },
        actionsWrapperSingleVerifyButton: {
            visible: isSignInButtonClicked === true
        },
        codeReceived: {
            label: phoneNumber !== '' ? translator(messages.verificationText, { num: phoneNumber }) : translator(messages.verificationTextWithOutNumber)
        },
        actionsWrapperSingleLink: {
            visible: isSignInButtonClicked !== true
        },
        actionsWrapperVerifySingleLink: {
            visible: isSignInButtonClicked === true
        },
    };

    const resolvers = {
        resolveValue: readValue,
        resolveCallbackMap: {
            login: login,
            forgotPassword: onForgotPassword || _.noop,
            sendVerificationCode: sendVerificationCode,
            resentVerificationCode: sendVerificationCode || _.noop
        },
        resolveClassNameMap: styles
    };

    const successRedirectPath = isSignInButtonClicked ? pathAfterSignIn : successPath;

    return (
        <Fragment>
            <RedirectAfterLogin successPath={successRedirectPath} loginPath={loginPath} />
            {renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers)}
        </Fragment>
    );
}

LoginComponent.propTypes = {
    onForgotPassword: PropTypes.func,
    successPath: PropTypes.string,
    loginPath: PropTypes.string,
    pathAfterSignIn: PropTypes.string
};

LoginComponent.defaultProps = {
    onForgotPassword: undefined,
    successPath: undefined,
    loginPath: undefined,
    pathAfterSignIn: undefined
};

export default LoginComponent;
